import { useState } from "react";
import { DndContext, DragOverlay, KeyboardSensor, MouseSensor, TouchSensor, useDraggable, useDroppable, useSensor, useSensors } from "@dnd-kit/core";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, CalendarClock, Eye } from "lucide-react";
import { DataTable, StatusBadge } from "@/components/common/ui-bits";
import { fmtDate, todayISO, TASK_STATUSES } from "@/lib/format";
import { getLang } from "@/i18n/translator";

const PRIO = { Urgente: "border-l-rose-500", Alta: "border-l-amber-500", Normal: "border-l-blue-500", Baja: "border-l-slate-300" };

function TaskCard({ t, onOpen, overlay }) {
  return (
    <div onClick={() => onOpen?.(t.id)} onKeyDown={(e) => e.key === "Enter" && onOpen?.(t.id)} role="button" tabIndex={-1}
      data-testid={overlay ? "kanban-drag-overlay" : `kanban-card-${t.id}`}
      className={`cursor-pointer select-none rounded-md border border-l-4 border-slate-200 bg-white p-3 text-sm shadow-sm transition-[box-shadow] duration-150 hover:shadow-md ${overlay ? "rotate-2 shadow-xl" : ""} ${PRIO[t.priority]}`}>
      <p className="font-medium text-slate-800">{t.title}</p>
      <p className="mt-1 truncate text-xs text-slate-500">{t.client_name}</p>
      <div className="mt-2 flex items-center gap-2 text-xs">
        {t.due_date && <span className={`inline-flex items-center gap-1 ${t.overdue ? "font-semibold text-rose-600" : "text-slate-500"}`}><CalendarClock className="h-3 w-3" />{fmtDate(t.due_date)}</span>}
        {t.visible_to_client && <Eye className="h-3 w-3 text-slate-400" aria-label="Visible para el cliente" />}
        <span className="ml-auto text-slate-400">{t.priority}</span>
      </div>
    </div>
  );
}

function DraggableCard({ t, onOpen }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: t.id });
  return (
    <div ref={setNodeRef} {...listeners} {...attributes} style={{ opacity: isDragging ? 0.35 : 1, touchAction: "manipulation" }} data-testid={`kanban-draggable-${t.id}`}>
      <TaskCard t={t} onOpen={onOpen} />
    </div>
  );
}

function Column({ s, tasks, onOpen }) {
  const { setNodeRef, isOver } = useDroppable({ id: s });
  return (
    <div ref={setNodeRef} data-testid={`kanban-column-${s}`}
      className={`flex w-[70vw] max-w-[18rem] shrink-0 snap-start flex-col rounded-lg border p-3 transition-colors duration-150 sm:w-72 ${isOver ? "border-blue-400 bg-blue-50" : "border-slate-200 bg-slate-100/60"}`}>
      <div className="mb-3 flex items-center justify-between"><StatusBadge status={s} /><span className="num text-xs text-slate-500">{tasks.length}</span></div>
      <div className="flex min-h-[80px] flex-col gap-2">{tasks.map((t) => <DraggableCard key={t.id} t={t} onOpen={onOpen} />)}</div>
    </div>
  );
}

export function KanbanView({ tasks, onOpen, onMove }) {
  const [active, setActive] = useState(null);
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 8 } }),
    useSensor(KeyboardSensor, { keyboardCodes: { start: ["Space"], cancel: ["Escape"], end: ["Space", "Enter"] } }),
  );
  const activeTask = tasks.find((t) => t.id === active);
  const coarse = typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches;
  const autoScroll = coarse ? { threshold: { x: 0.02, y: 0.04 }, acceleration: 1 } : { threshold: { x: 0.06, y: 0.06 }, acceleration: 2 };
  return (
    <DndContext sensors={sensors} autoScroll={autoScroll} onDragStart={(e) => setActive(e.active.id)} onDragCancel={() => setActive(null)}
      onDragEnd={(e) => { setActive(null); if (e.over) onMove(e.active.id, e.over.id); }}>
      <p className="mb-2 text-xs text-slate-500 lg:hidden">En teléfono o tablet: mantenga presionada una tarjeta y arrástrela a otra columna.</p>
      <div className="flex snap-x gap-4 overflow-x-auto pb-4" data-testid="kanban-board">
        {TASK_STATUSES.map((s) => <Column key={s} s={s} tasks={tasks.filter((t) => t.status === s)} onOpen={onOpen} />)}
      </div>
      <DragOverlay>{activeTask ? <TaskCard t={activeTask} overlay /> : null}</DragOverlay>
    </DndContext>
  );
}

export function ListView({ tasks, onOpen, name }) {
  return (
    <DataTable testid="tasks-table" rows={tasks} onRowClick={(r) => onOpen(r.id)} empty="No hay tareas."
      columns={[
        { key: "title", label: "Tarea", render: (r) => <span className="font-medium text-slate-900">{r.title}<span className="block text-xs font-normal text-slate-500">{r.project_name || r.client_name}</span></span> },
        { key: "assignee_id", label: "Responsable", render: (r) => name(r.assignee_id) },
        { key: "priority", label: "Prioridad", render: (r) => <StatusBadge status={r.priority} /> },
        { key: "due_date", label: "Vence", render: (r) => <span className={r.overdue ? "font-semibold text-rose-600" : ""}>{fmtDate(r.due_date)}</span> },
        { key: "visible_to_client", label: "Visibilidad", render: (r) => (r.visible_to_client ? "Cliente" : "Interna") },
        { key: "status", label: "Estado", render: (r) => <StatusBadge status={r.status} /> },
      ]} />
  );
}

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export function CalendarView({ tasks, onOpen }) {
  const [cursor, setCursor] = useState(() => { const [y, m] = todayISO().split("-"); return new Date(Number(y), Number(m) - 1, 1); });
  const start = new Date(cursor);
  start.setDate(1 - ((cursor.getDay() + 6) % 7));
  const days = Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  const today = todayISO();
  const shift = (n) => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + n, 1));
  return (
    <div className="card-box p-4" data-testid="calendar-view">
      <div className="mb-3 flex items-center justify-between">
        <Button size="icon" variant="ghost" onClick={() => shift(-1)} aria-label="Mes anterior" data-testid="calendar-prev"><ChevronLeft className="h-4 w-4" /></Button>
        <h3 className="font-display font-bold" data-testid="calendar-month">{(() => { const s = cursor.toLocaleDateString(getLang() === "en" ? "en-US" : "es-US", { month: "long", year: "numeric" }); return s.charAt(0).toUpperCase() + s.slice(1); })()}</h3>
        <Button size="icon" variant="ghost" onClick={() => shift(1)} aria-label="Mes siguiente" data-testid="calendar-next"><ChevronRight className="h-4 w-4" /></Button>
      </div>
      <div className="grid grid-cols-7 gap-px overflow-hidden rounded-md bg-slate-200 text-xs">
        {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((d) => <div key={d} className="bg-slate-50 p-2 text-center font-semibold text-slate-500">{d}</div>)}
        {days.map((d) => {
          const k = iso(d);
          const list = tasks.filter((t) => t.due_date === k);
          return (
            <div key={k} className={`min-h-[90px] bg-white p-1.5 ${d.getMonth() !== cursor.getMonth() ? "opacity-40" : ""}`}>
              <span className={`mb-1 inline-block rounded px-1 ${k === today ? "bg-blue-600 text-white" : "text-slate-500"}`}>{d.getDate()}</span>
              {list.slice(0, 3).map((t) => (
                <button key={t.id} onClick={() => onOpen(t.id)} className={`mb-0.5 block w-full truncate rounded px-1 py-0.5 text-left ${t.overdue ? "bg-rose-50 text-rose-700" : "bg-blue-50 text-blue-800"}`} data-testid={`calendar-task-${t.id}`}>{t.title}</button>
              ))}
              {list.length > 3 && <span className="text-slate-400">+{list.length - 3}</span>}
            </div>
          );
        })}
      </div>
      <p className="mt-2 text-xs text-slate-400">Se muestran las tareas según su fecha de vencimiento.</p>
    </div>
  );
}

export function TimelineView({ tasks, onOpen }) {
  const dated = tasks.filter((t) => t.start_date || t.due_date);
  if (!dated.length) return <div className="card-box p-8 text-center text-sm text-slate-400" data-testid="timeline-view">Asigne fechas de inicio o vencimiento para ver el cronograma.</div>;
  const toN = (s) => new Date(`${s}T12:00:00`).getTime();
  const starts = dated.map((t) => toN(t.start_date || t.due_date));
  const ends = dated.map((t) => toN(t.due_date || t.start_date));
  const min = Math.min(...starts, toN(todayISO()));
  const max = Math.max(...ends, toN(todayISO())) + 864e5;
  const pct = (n) => ((n - min) / (max - min)) * 100;
  return (
    <div className="card-box overflow-x-auto p-4" data-testid="timeline-view">
      <div className="min-w-[640px] space-y-2">
        <div className="flex justify-between text-xs text-slate-400"><span>{fmtDate(new Date(min).toISOString())}</span><span>{fmtDate(new Date(max).toISOString())}</span></div>
        {dated.map((t, i) => {
          const s = starts[i];
          const e = Math.max(ends[i], s) + 864e5;
          return (
            <div key={t.id} className="relative grid grid-cols-[200px_1fr] items-center gap-3">
              <button onClick={() => onOpen(t.id)} className="truncate text-left text-sm text-slate-700 hover:text-blue-700">{t.title}</button>
              <div className="relative h-6 rounded bg-slate-100">
                <div className="absolute inset-y-0 w-px bg-rose-400" style={{ left: `${pct(toN(todayISO()))}%` }} />
                <button onClick={() => onOpen(t.id)} data-testid={`timeline-bar-${t.id}`} className={`absolute inset-y-1 rounded ${t.status === "Completada" ? "bg-emerald-500" : t.overdue ? "bg-rose-500" : "bg-blue-600"}`}
                  style={{ left: `${pct(s)}%`, width: `${Math.max(pct(e) - pct(s), 1.5)}%` }} aria-label={t.title} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
